import React, { FC, useEffect } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { PhotoPickerDialogProps, PICKER_TYPES } from './PhotoPickerDialog.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { ScrollWrapper } from './PhotoPickerDialog.styles';
import { DialogFooter, PhotoPickerLibrary } from './components';
import { useWebsiteOverviewTelemetry } from '@apps/admin/routes/WebsiteOverview/WebsiteOverview.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';

export const PhotoPickerDialog: FC<PhotoPickerDialogProps> = props => {
  const {
    isOpen,
    handleClose,
    handleOpenFilePicker,
    isFetching,
    media,
    handleAddMedia,
    isAddingMedia,
    collectionId,
    showCount = false,
    pickerType = PICKER_TYPES.ADD_TO_COLLECTION,
    maxCount,
    minCount,
    photoSelectionHelperText,
    selectedItems = [],
    disabledItems = [],
    uploadInProgressCount = 0,
  } = props;

  const [selectedItemsId, setSelectedItemsId] = React.useState<string[]>([]);

  useEffect(() => {
    setSelectedItemsId(selectedItems);
  }, [selectedItems, isOpen]);

  const telemetry = useWebsiteOverviewTelemetry();

  const { eventInfo } = useEventInfo();

  const clearSelectedItemsAndClose = useEventCallback(() => {
    setSelectedItemsId([]);
  });

  const onCloseDialog = useEventCallback(() => {
    handleClose();
    clearSelectedItemsAndClose();
  });

  const onOpenFilePicker = useEventCallback(() => {
    pickerType === PICKER_TYPES.ADD_TO_COLLECTION && handleClose();
    handleOpenFilePicker();
  });

  const updateSelectedItemsId = useEventCallback((newSelectedItemId: string) => {
    setSelectedItemsId(prevSelectedItemsId => {
      if (prevSelectedItemsId.includes(newSelectedItemId)) {
        return prevSelectedItemsId.filter(id => id !== newSelectedItemId);
      }
      if (maxCount && selectedItemsId.length >= maxCount) {
        // If max count is reached, remove last selected item and add new item at the end
        return [...prevSelectedItemsId.slice(0, maxCount - 1), newSelectedItemId];
      }
      return [...prevSelectedItemsId, newSelectedItemId];
    });
  });

  const handleSelectAll = useEventCallback(() => {
    telemetry.selectAllPhotosCTAClicked(eventInfo?.eventId || '', collectionId || 'allPhotos');
    const allPhotoIds = media?.map(m => (m.__typename === 'MediaPhoto' ? m.id : '')).filter(id => !!id) || [];
    setSelectedItemsId(allPhotoIds);
  });

  const handleDeselectAll = useEventCallback(() => {
    telemetry.deselectAllPhotosCTAClicked(eventInfo?.eventId || '', collectionId || 'allPhotos');
    setSelectedItemsId([]);
  });

  const handleAdd = useEventCallback(() => {
    telemetry.addMediaFromMediaLibrary(eventInfo?.eventId || '', collectionId || 'allPhotos', selectedItemsId);
    handleAddMedia(selectedItemsId, clearSelectedItemsAndClose);
  });

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onCloseDialog}
      hideDivider={true}
      removeBodyPadding
      hasCloseButton={false}
      disableFocusLock={true}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingX: 0, paddingY: 5, overflow: 'auto', maxHeight: '100vh' } },
      }}
      dialogOverrides={{
        Content: { props: { width: '100%', maxWidth: pxToRem(788) } },
        Body: { props: { paddingX: 0, paddingY: 5, overflow: 'auto', maxHeight: 'calc(100vh - 160px)' } },
        Footer: { props: { paddingY: 5 } },
      }}
      renderFooter={() => (
        <DialogFooter
          handleDeselect={handleDeselectAll}
          handleSelectAll={handleSelectAll}
          handleCancel={onCloseDialog}
          handleAdd={handleAdd}
          isAdding={isAddingMedia}
          disableAddCTA={selectedItemsId.length === 0 || (!!minCount && selectedItemsId.length < minCount)}
          disableSelectAllCTA={selectedItemsId.length === media?.length}
          disableDeselectAllCTA={selectedItemsId.length === 0}
          pickerType={pickerType}
          selectedItemsCount={selectedItemsId.length}
        />
      )}
    >
      <ScrollWrapper>
        <PhotoPickerLibrary
          handleClose={onCloseDialog}
          isFetching={isFetching}
          media={media}
          selectedItemsId={selectedItemsId}
          disabledItems={disabledItems}
          updateSelectedItemsId={updateSelectedItemsId}
          handleOpenFilePicker={onOpenFilePicker}
          showCount={showCount}
          pickerType={pickerType}
          photoSelectionHelperText={photoSelectionHelperText}
          uploadInProgressCount={uploadInProgressCount}
        />
      </ScrollWrapper>
    </ResponsiveDialog>
  );
};

PhotoPickerDialog.displayName = 'PhotoPickerDialog';
